/**
 * FormTrendIndicator - Shows improving/declining/stable form
 * Phase 13.6 - Form Streaks & Trends
 */
import React from 'react'
import { IoTrendingUp, IoTrendingDown, IoRemove } from 'react-icons/io5'

interface FormTrendIndicatorProps {
  trend: 'improving' | 'declining' | 'stable'
  trendScore?: number
  showLabel?: boolean
  compact?: boolean
}

const TREND_CONFIG = {
  improving: {
    icon: IoTrendingUp,
    label: 'Improving',
    color: 'var(--green)',
    bgColor: 'rgba(34, 197, 94, 0.15)',
  },
  declining: {
    icon: IoTrendingDown,
    label: 'Declining',
    color: 'var(--red)',
    bgColor: 'rgba(239, 68, 68, 0.15)',
  },
  stable: {
    icon: IoRemove,
    label: 'Stable',
    color: 'var(--muted)',
    bgColor: 'var(--surface-hover)',
  },
} as const

export function FormTrendIndicator({
  trend,
  trendScore,
  showLabel = true,
  compact = false,
}: FormTrendIndicatorProps) {
  const config = TREND_CONFIG[trend]
  const Icon = config.icon

  const tooltip = trendScore !== undefined
    ? `${config.label} (${trendScore > 0 ? '+' : ''}${(trendScore * 100).toFixed(0)}%)`
    : config.label

  return (
    <span
      className={`form-trend-indicator ${trend} ${compact ? 'compact' : ''}`}
      style={{
        color: config.color,
        backgroundColor: config.bgColor,
      }}
      title={tooltip}
    >
      <Icon size={compact ? 12 : 14} />
      {showLabel && !compact && <span>{config.label}</span>}
    </span>
  )
}

interface FormSummaryProps {
  points: number
  wins: number
  draws: number
  losses: number
  showPoints?: boolean
}

export function FormSummary({
  points,
  wins,
  draws,
  losses,
  showPoints = true,
}: FormSummaryProps) {
  const total = wins + draws + losses
  if (total === 0) return null

  return (
    <span className="form-summary">
      <span className="form-summary-record">
        <span className="wins">{wins}W</span>
        <span className="draws">{draws}D</span>
        <span className="losses">{losses}L</span>
      </span>
      {showPoints && (
        <span className="form-summary-points">
          ({points}pts)
        </span>
      )}
    </span>
  )
}

interface FormAdvantageIndicatorProps {
  advantage: 'home' | 'away' | 'even'
  advantageScore: number
  homeName: string
  awayName: string
}

export function FormAdvantageIndicator({
  advantage,
  advantageScore,
  homeName,
  awayName,
}: FormAdvantageIndicatorProps) {
  if (advantage === 'even') {
    return (
      <span className="form-advantage even">
        Even form
      </span>
    )
  }

  const teamName = advantage === 'home' ? homeName : awayName
  const absScore = Math.abs(advantageScore)

  let strength = 'slight'
  if (absScore >= 4) strength = 'strong'
  else if (absScore >= 2) strength = 'moderate'

  return (
    <span className={`form-advantage ${advantage}`}>
      <span className="team-name">{teamName}</span>
      <span className="advantage-label">{strength} form advantage</span>
    </span>
  )
}
